import { useId, useState } from 'react';
import type * as React from 'react';
import { Link } from 'react-router';
import { Page, Notice } from '@/components/page';
import { DataTable, type Column } from '@/components/data-table';
import { CursorFooter } from '@/components/cursor-footer';
import { CardSkeleton, EmptyState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Label, NativeSelect } from '@/components/ui/field';
import { Badge, Card, CardBody, Tooltip } from '@/components/ui/primitives';
import { RESOURCES, type SearchAnomaly } from '@/api/resources';
import { useCursorPage } from '@/hooks/use-cursor-page';
import { formatNumber, formatRelative } from '@/lib/format';

/**
 * Search anomalies (06 §2) — `SUPPORT` and `SUPER_ADMIN`.
 *
 * ⚠️ **This is the detection mechanism for child enumeration, not a dashboard
 * widget** (CLAUDE.md §4.2). Search is substring over open registration and
 * returns children *with photographs*; the exact two-token matching that used to
 * bound the exposure no longer exists. What bounds it now is a 3-character
 * floor, 20 searches a day, a 50-row page cap — and somebody reading this
 * screen. That is why it is laid out as a signal rather than a table of numbers:
 * high volume with a low accept rate is the pattern, and a raw grid buries it.
 *
 * ⚠️ Roles are enforced by the server. `AdminModerationController` carries
 * `@AdminRoles('SUPER_ADMIN', 'SUPPORT')`; `ANALYST` gets `403 INSUFFICIENT_ROLE`
 * here regardless of what the sidebar drew.
 *
 * ⚠️ `acceptRate: null` is the strongest reading on this screen, not a missing
 * value. Hundreds of searches and not one connection request is enumeration with
 * no social pretext at all, so it sorts and colours as the worst case rather
 * than rendering as an em dash.
 */

const WINDOWS = [7, 30, 90] as const;
const MIN_SEARCHES = [10, 20, 50, 100] as const;
const ACCEPT_RATES = [
  { value: '', label: 'Any accept rate' },
  { value: '0', label: 'Nothing accepted (0%)' },
  { value: '0.1', label: 'Under 10%' },
  { value: '0.25', label: 'Under 25%' },
  { value: '0.5', label: 'Under 50%' },
] as const;

type Severity = 'enumerating' | 'low-accept' | 'volume';

const SEVERITY: Record<
  Severity,
  { label: string; tone: 'danger' | 'caution' | 'neutral'; hint: string }
> = {
  enumerating: {
    label: 'No requests at all',
    tone: 'danger',
    hint: 'Searched repeatedly and never once asked to connect. There is no social explanation for this shape.',
  },
  'low-accept': {
    label: 'Rarely accepted',
    tone: 'caution',
    hint: 'Sends requests, but guardians mostly decline. Volume with a low accept rate is the pattern that matters.',
  },
  volume: {
    label: 'High volume',
    tone: 'neutral',
    hint: 'Searching a lot, and guardians are accepting. Often an enthusiastic parent rather than a problem.',
  },
};

function severityOf(row: SearchAnomaly): Severity {
  if (row.acceptRate === null) return 'enumerating';
  if (row.acceptRate <= 0.25) return 'low-accept';
  return 'volume';
}

export function SearchAnomaliesPage(): React.JSX.Element {
  const [days, setDays] = useState<number>(30);
  const [minSearches, setMinSearches] = useState<number>(20);
  const [maxAcceptRate, setMaxAcceptRate] = useState<string>('');

  const daysId = useId();
  const minId = useId();
  const rateId = useId();

  const page = useCursorPage<SearchAnomaly>(RESOURCES.searchAnomalies, {
    days,
    minSearches,
    maxAcceptRate: maxAcceptRate === '' ? undefined : Number(maxAcceptRate),
    limit: 25,
  });

  const rows = page.rows ?? [];
  const busiest = rows.reduce((max, row) => Math.max(max, row.searchCount), 0);

  const columns: Array<Column<SearchAnomaly>> = [
    {
      key: 'signal',
      header: 'Signal',
      width: '11rem',
      sortValue: (row) => severityOf(row),
      render: (row) => {
        const severity = SEVERITY[severityOf(row)];
        return (
          <Tooltip label={severity.hint}>
            <span>
              <Badge tone={severity.tone}>{severity.label}</Badge>
            </span>
          </Tooltip>
        );
      },
    },
    {
      key: 'account',
      header: 'Account',
      sortValue: (row) => `${row.lastName} ${row.firstName}`,
      render: (row) => (
        <Link to={`/users/${row.userId}`} className="rounded hover:underline">
          <span className="block font-medium text-ink">
            {row.firstName} {row.lastName}
          </span>
          <span className="block text-xs text-ink-soft">{row.email}</span>
        </Link>
      ),
    },
    {
      key: 'searchCount',
      header: 'Searches',
      align: 'right',
      width: '13rem',
      sortValue: (row) => row.searchCount,
      render: (row) => <VolumeBar value={row.searchCount} max={busiest} />,
    },
    {
      key: 'distinctQueries',
      header: 'Distinct',
      align: 'right',
      hideBelow: 'lg',
      width: '9rem',
      sortValue: (row) => (row.searchCount === 0 ? 0 : row.distinctQueries / row.searchCount),
      render: (row) => <DistinctCell row={row} />,
    },
    {
      key: 'acceptRate',
      header: 'Accepted',
      align: 'right',
      width: '11rem',
      // ⚠️ `null` sorts as the worst case, not as zero-and-therefore-missing.
      // Descending by this column has to put "never asked to connect" first.
      sortValue: (row) => (row.acceptRate === null ? -1 : row.acceptRate),
      render: (row) => <AcceptCell row={row} />,
    },
    {
      key: 'totalResults',
      header: 'Children shown',
      align: 'right',
      hideBelow: 'xl',
      sortValue: (row) => row.totalResults,
      render: (row) => (
        <Tooltip label="Sum of result counts — an upper bound on the child cards this account has been shown, counting repeats.">
          <span className="text-ink">{formatNumber(row.totalResults)}</span>
        </Tooltip>
      ),
    },
    {
      key: 'lastSearchAt',
      header: 'Last search',
      hideBelow: 'md',
      width: '9rem',
      sortValue: (row) => row.lastSearchAt,
      render: (row) => (
        <time dateTime={row.lastSearchAt} className="text-xs text-ink-soft">
          {formatRelative(row.lastSearchAt)}
        </time>
      ),
    },
  ];

  return (
    <Page
      title="Search anomalies"
      description="Accounts working through the child directory rather than looking for someone."
      breadcrumbs={[{ label: 'People' }, { label: 'Search anomalies' }]}
      width="wide"
    >
      <div className="space-y-5">
        <Notice title="This screen is the control, not a report">
          Search matches children by substring from three characters and returns their photographs,
          and anyone with an email address can register. A 3-character floor, 20 searches a day and
          a 50-row page cap are the only other limits — so an account that searches constantly and
          never connects is the signal this panel exists to surface.
        </Notice>

        <Summary rows={rows} isLoading={page.isLoading} days={days} />

        <div className="flex flex-wrap items-end gap-3 rounded-card border border-line bg-surface px-4 py-3">
          <div className="space-y-1.5">
            <Label htmlFor={daysId}>Window</Label>
            <NativeSelect
              id={daysId}
              className="w-40"
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
            >
              {WINDOWS.map((value) => (
                <option key={value} value={value}>
                  Last {value} days
                </option>
              ))}
            </NativeSelect>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={minId}>Minimum searches</Label>
            <NativeSelect
              id={minId}
              className="w-40"
              value={minSearches}
              onChange={(event) => setMinSearches(Number(event.target.value))}
            >
              {MIN_SEARCHES.map((value) => (
                <option key={value} value={value}>
                  {value}+ searches
                </option>
              ))}
            </NativeSelect>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={rateId}>Accept rate at most</Label>
            <NativeSelect
              id={rateId}
              className="w-52"
              value={maxAcceptRate}
              onChange={(event) => setMaxAcceptRate(event.target.value)}
            >
              {ACCEPT_RATES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </NativeSelect>
          </div>

          <p className="ml-auto max-w-xs text-xs text-ink-faint">
            Accounts that sent no requests at all have no rate to compare, and always match this
            filter.
          </p>
        </div>

        <DataTable
          label="Search anomalies"
          columns={columns}
          rows={page.rows}
          rowKey={(row) => row.userId}
          isLoading={page.isLoading}
          error={page.error}
          onRetry={page.refetch}
          endpoint="GET /admin/search-anomalies"
          empty={
            <EmptyState
              title="Nobody crosses this threshold"
              description="No account searched that much over this window. Lower the minimum, or widen the window, before concluding there is nothing to see."
              action={
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDays(90);
                    setMinSearches(10);
                    setMaxAcceptRate('');
                  }}
                >
                  Widen to 90 days and 10 searches
                </Button>
              }
            />
          }
          footer={
            <CursorFooter
              page={page}
              note="Ranked by search volume. Sort by Accepted to bring the accounts that never connect to the top."
            />
          }
        />
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------------------

/**
 * The four numbers worth reading before the table.
 *
 * ⚠️ Every one is computed from the page in hand, and the labels say so. The API
 * is cursor-paginated with no total (CLAUDE.md §5), so a headline claiming "412
 * accounts flagged" across the whole window would be a number this screen cannot
 * know.
 */
function Summary({
  rows,
  isLoading,
  days,
}: {
  rows: SearchAnomaly[];
  isLoading: boolean;
  days: number;
}): React.JSX.Element {
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>
    );
  }

  const neverConnected = rows.filter((row) => row.acceptRate === null).length;
  const searches = rows.reduce((sum, row) => sum + row.searchCount, 0);
  const shown = rows.reduce((sum, row) => sum + row.totalResults, 0);

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Tile
        label="On this page"
        value={formatNumber(rows.length)}
        hint={`Accounts over the threshold in the last ${String(days)} days`}
      />
      <Tile
        label="Never connected"
        value={formatNumber(neverConnected)}
        hint="Searched repeatedly, sent no requests at all"
        tone={neverConnected > 0 ? 'danger' : 'neutral'}
      />
      <Tile
        label="Searches"
        value={formatNumber(searches)}
        hint="Total across the accounts on this page"
      />
      <Tile
        label="Child cards shown"
        value={formatNumber(shown)}
        hint="Upper bound, counting repeats"
      />
    </div>
  );
}

function Tile({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  hint: string;
  tone?: 'neutral' | 'danger';
}): React.JSX.Element {
  return (
    <Card className={tone === 'danger' ? 'border-danger/40' : undefined}>
      <CardBody>
        <p className="text-2xs font-semibold tracking-wider text-ink-faint uppercase">{label}</p>
        <p
          className={`mt-2 text-3xl leading-9 font-semibold tracking-tight ${
            tone === 'danger' ? 'text-danger' : 'text-ink'
          }`}
          data-numeric
        >
          {value}
        </p>
        <p className="mt-1 text-xs text-ink-soft">{hint}</p>
      </CardBody>
    </Card>
  );
}

/** Volume as a bar as well as a number, so the outlier is visible without reading. */
function VolumeBar({ value, max }: { value: number; max: number }): React.JSX.Element {
  const width = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
  return (
    <span className="flex items-center justify-end gap-2">
      <span
        className="h-1.5 w-24 overflow-hidden rounded-full bg-surface-sunken"
        aria-hidden="true"
      >
        <span className="block h-full rounded-full bg-primary" style={{ width: `${width}%` }} />
      </span>
      <span className="w-12 text-right font-medium text-ink" data-numeric>
        {formatNumber(value)}
      </span>
    </span>
  );
}

/**
 * Distinct queries against total searches.
 *
 * A ratio near 1 means almost every search was a different string — working
 * through names rather than looking for one person, which is the difference
 * between a determined parent and an enumeration.
 */
function DistinctCell({ row }: { row: SearchAnomaly }): React.JSX.Element {
  const ratio = row.searchCount === 0 ? 0 : row.distinctQueries / row.searchCount;
  return (
    <Tooltip
      label={
        ratio >= 0.8
          ? 'Nearly every search was a different string — working through names, not looking for one person.'
          : 'Repeating the same searches, which is what looking for one person looks like.'
      }
    >
      <span className={ratio >= 0.8 ? 'font-medium text-caution' : 'text-ink-soft'} data-numeric>
        {formatNumber(row.distinctQueries)}
        <span className="text-ink-faint"> / {formatNumber(row.searchCount)}</span>
      </span>
    </Tooltip>
  );
}

/**
 * ⚠️ `acceptRate === null` renders as words, not as a dash.
 *
 * An em dash reads as "no data" and gets skipped. "Never asked" reads as what it
 * is: the account searched and did not once try to connect.
 */
function AcceptCell({ row }: { row: SearchAnomaly }): React.JSX.Element {
  if (row.acceptRate === null) {
    return (
      <span className="inline-flex flex-col items-end">
        <span className="text-sm font-semibold text-danger">Never asked</span>
        <span className="text-2xs text-ink-faint">0 requests sent</span>
      </span>
    );
  }

  const percent = Math.round(row.acceptRate * 100);
  return (
    <span className="inline-flex flex-col items-end">
      <span
        className={`text-sm font-semibold ${percent <= 25 ? 'text-caution' : 'text-ink'}`}
        data-numeric
      >
        {percent}%
      </span>
      <span className="text-2xs text-ink-faint" data-numeric>
        {formatNumber(row.requestsAccepted)} of {formatNumber(row.requestsSent)}
      </span>
    </span>
  );
}
